// src/services/prescriptionApi.js
//
// Prescription endpoints for the doctor portal (contract:
// docs/visit-workflow-contract.md, "Prescription"). Responses are unwrapped to
// the body. The PDF helpers go through the shared axios instance so the JWT is
// attached; a plain <a href> to the API would be unauthenticated.

import api from './api';

const body = res => res.data;
// 403 = "not your prescription", not an expired session (see services/api.js).
const FORBID = { treat403AsForbidden: true };

/** GET /doctor/drugs?q= (min 2 chars, ≤20) → [{id, name, strength, form, genericName}] */
export const searchDrugs = (q, options = {}) =>
  api.get('/doctor/drugs', { params: { q }, ...FORBID, ...options }).then(res => (Array.isArray(res.data) ? res.data : []));

/** POST /doctor/prescriptions → Prescription (DRAFT) */
export const createPrescription = payload => api.post('/doctor/prescriptions', payload, FORBID).then(body);

/** PUT /doctor/prescriptions/{id} → Prescription; replaces items, DRAFT only. */
export const updatePrescription = (id, payload) => api.put(`/doctor/prescriptions/${id}`, payload, FORBID).then(body);

export const getPrescription = id => api.get(`/doctor/prescriptions/${id}`, FORBID).then(body);

// `options` lets a caller bound the wait (the patient record passes a load timeout so a hung call becomes an error with Retry).
export const listPatientPrescriptions = (patientId, options = {}) =>
  api.get(`/doctor/patients/${patientId}/prescriptions`, { ...FORBID, ...options }).then(res => (Array.isArray(res.data) ? res.data : []));

/** POST /doctor/prescriptions/check → {allergyConflicts: [{drug, allergy}], duplicates: [drug]} (advisory only) */
export const checkPrescription = (patientId, drugNames, options = {}) =>
  api.post('/doctor/prescriptions/check', { patientId, drugNames }, { ...FORBID, ...options }).then(body);

/** POST /doctor/prescriptions/{id}/sign → Prescription (SIGNED, idempotent) */
export const signPrescription = id => api.post(`/doctor/prescriptions/${id}/sign`, null, FORBID).then(body);

/** GET /doctor/prescriptions/{id}/pdf → Blob (application/pdf) */
export const fetchPrescriptionPdf = id =>
  api.get(`/doctor/prescriptions/${id}/pdf`, { ...FORBID, responseType: 'blob' }).then(res => res.data);

/**
 * Fetch the PDF with auth and open it in a new tab, or download it when
 * popups are blocked. The object URL is revoked after a minute.
 */
export async function openPrescriptionPdf(id, filename = 'prescription.pdf') {
  const blob = await fetchPrescriptionPdf(id);
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  // No 'noopener' feature: it makes window.open() return null even when the tab
  // opened, which would ALSO trigger the download fallback below. The PDF is a
  // same-origin blob, so severing the opener buys nothing.
  const win = window.open(url, '_blank');
  if (win) win.opener = null;
  else {
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
